import { useId } from 'react';

import type { LinkedListView, Tone } from '../types';
import { CANVAS, GEO, toneColor, toneFill } from '../theme';

import { Caption, EmptyCanvas, FONT, LAYOUT, PointerTip, fmt, frameProps } from './shared';

const NODE_W = 68;
const NODE_H = 44;
const ARROW_GAP = 40;
const TERMINATOR_W = 30;
const ARC_BAND = 50;
const PAD = GEO.padding;

export function LinkedListRenderer({ view }: { view: LinkedListView }) {
  const nodes = view.nodes;
  const title = view.caption ?? (nodes.length === 0 ? 'Empty linked list' : `Linked list of ${nodes.length} nodes`);
  const rawId = useId();
  const arrowId = `ll-arrow-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;

  if (nodes.length === 0) return <EmptyCanvas title={title} />;

  const indexOf = new Map<string, number>();
  nodes.forEach((node, i) => {
    if (!indexOf.has(node.id)) indexOf.set(node.id, i);
  });

  const marks = new Map<string, { tone: Tone; label?: string }>();
  for (const mark of view.marks ?? []) {
    if (indexOf.has(mark.nodeId)) marks.set(mark.nodeId, { tone: mark.tone, label: mark.label });
  }
  const hasMarkLabel = [...marks.values()].some((m) => m.label);

  /** Index used to park pointers whose `nodeId` is null, at the ∅ position. */
  const NULL_SLOT = -1;

  const pointerGroups = new Map<number, Array<{ name: string; tone?: Tone }>>();
  for (const pointer of view.pointers ?? []) {
    let i: number | undefined;
    if (pointer.nodeId === null) {
      i = NULL_SLOT;
    } else {
      i = indexOf.get(pointer.nodeId);
      if (i === undefined) continue;
    }
    const group = pointerGroups.get(i) ?? [];
    group.push(pointer);
    pointerGroups.set(i, group);
  }
  const pointerDepth = Math.max(0, ...[...pointerGroups.values()].map((g) => g.length));

  /**
   * Every node whose `next` is null terminates, not just the last one in display
   * order — after a reversal the new tail sits at the front of the array, and a
   * missing ∅ there is indistinguishable from "no next field drawn at all".
   */
  const terminates = nodes.map((node) => node.next === null || node.next === undefined);
  const lastTerminates = terminates[nodes.length - 1];
  const needsNullSlot = pointerGroups.has(NULL_SLOT);

  /**
   * Any `next` that does not land on the immediately following node is drawn as
   * an arc beneath the row. A straight line to a non-adjacent node would pass
   * behind the nodes in between, so a splice that bypasses a deleted node would
   * read as an edge running through it — the opposite of what happened.
   */
  const edges = nodes.flatMap((node, i) => {
    if (!node.next) return [];
    const target = indexOf.get(node.next);
    if (target === undefined) return [];
    return [{ from: i, to: target, arc: target !== i + 1, span: Math.abs(target - i) }];
  });
  const arcs = edges.filter((edge) => edge.arc);
  /** Deeper curves for longer jumps, so concurrent arcs do not sit on top of each other. */
  const arcDepth = (span: number) => ARC_BAND * (1.05 + 0.32 * Math.min(span - 1, 4));
  const maxArcDepth = arcs.length ? Math.max(...arcs.map((e) => arcDepth(e.span))) : 0;

  let cursor = PAD;
  let captionY = 0;
  if (view.caption) {
    captionY = cursor + 8;
    cursor += LAYOUT.captionBand;
  }
  const pointerTop = cursor;
  cursor += pointerDepth * LAYOUT.pointerRow;
  const rowY = cursor;
  cursor += NODE_H;
  if (hasMarkLabel) cursor += LAYOUT.labelBand;
  cursor += maxArcDepth;

  const height = cursor + PAD;
  const tailSlot = lastTerminates || needsNullSlot;
  const width =
    PAD * 2 + nodes.length * NODE_W + (nodes.length - 1) * ARROW_GAP + (tailSlot ? ARROW_GAP + TERMINATOR_W : 0);

  const x = (i: number) => PAD + i * (NODE_W + ARROW_GAP);
  const cx = (i: number) => x(i) + NODE_W / 2;
  const midY = rowY + NODE_H / 2;
  const bottomY = rowY + NODE_H;
  /** Centre of the ∅ slot past the last node — also where null pointers park. */
  const nullX = x(nodes.length - 1) + NODE_W + ARROW_GAP + TERMINATOR_W / 2;

  return (
    <svg {...frameProps(width, height, view.caption)}>
      <title>{title}</title>
      <defs aria-hidden="true">
        <marker
          id={arrowId}
          markerWidth={8}
          markerHeight={8}
          refX={7}
          refY={4}
          orient="auto"
          markerUnits="strokeWidth"
        >
          <path d="M 0 0 L 8 4 L 0 8 z" fill={CANVAS.textDim} />
        </marker>
      </defs>

      {view.caption ? <Caption text={view.caption} x={PAD} y={captionY} /> : null}

      <g aria-hidden="true">
        {edges.map((edge, k) =>
          edge.arc ? (
            <path
              key={`edge-${k}`}
              d={`M ${cx(edge.from)} ${bottomY} Q ${(cx(edge.from) + cx(edge.to)) / 2} ${bottomY + arcDepth(edge.span)} ${cx(edge.to)} ${bottomY + 4}`}
              fill="none"
              stroke={CANVAS.textDim}
              strokeWidth={1.5}
              markerEnd={`url(#${arrowId})`}
            />
          ) : (
            <line
              key={`edge-${k}`}
              x1={x(edge.from) + NODE_W}
              y1={midY}
              x2={x(edge.to) - 8}
              y2={midY}
              stroke={CANVAS.textDim}
              strokeWidth={1.5}
              markerEnd={`url(#${arrowId})`}
            />
          ),
        )}
        {terminates.map((isEnd, i) => {
          if (!isEnd) return null;
          const isLast = i === nodes.length - 1;
          // The last node terminates into its own reserved slot; an interior
          // node (a detached fragment, or a new tail after a reversal) shows a
          // compact ∅ inside the gap to its right.
          const arrowEnd = isLast ? x(i) + NODE_W + ARROW_GAP - 4 : x(i) + NODE_W + ARROW_GAP / 2 - 10;
          const glyphX = isLast ? nullX : x(i) + NODE_W + ARROW_GAP / 2 + 3;
          return (
            <g key={`term-${i}`}>
              <line
                x1={x(i) + NODE_W}
                y1={midY}
                x2={arrowEnd}
                y2={midY}
                stroke={CANVAS.textDim}
                strokeWidth={1.5}
                markerEnd={`url(#${arrowId})`}
              />
              <text
                x={glyphX}
                y={midY}
                fill={CANVAS.textFaint}
                fontSize={isLast ? FONT.value : FONT.index}
                textAnchor="middle"
                dominantBaseline="central"
              >
                ∅
              </text>
            </g>
          );
        })}
        {/* A ∅ to park null pointers against when the tail itself does not end. */}
        {needsNullSlot && !lastTerminates ? (
          <text
            x={nullX}
            y={midY}
            fill={CANVAS.textFaint}
            fontSize={FONT.value}
            textAnchor="middle"
            dominantBaseline="central"
          >
            ∅
          </text>
        ) : null}
      </g>

      {nodes.map((node, i) => {
        const mark = marks.get(node.id);
        const tone = mark?.tone;
        return (
          <g key={`${node.id}-${i}`}>
            <rect
              x={x(i)}
              y={rowY}
              width={NODE_W}
              height={NODE_H}
              rx={8}
              fill={tone ? toneFill(tone) : CANVAS.cell}
              stroke={tone ? toneColor(tone) : CANVAS.cellBorder}
              strokeWidth={1.5}
            />
            <text
              x={cx(i)}
              y={midY}
              fill={CANVAS.text}
              fontSize={FONT.value}
              textAnchor="middle"
              dominantBaseline="central"
            >
              {fmt(node.value)}
            </text>
            {mark?.label ? (
              <text
                x={cx(i)}
                y={bottomY + LAYOUT.labelBand / 2 + 2}
                fill={toneColor(tone)}
                fontSize={FONT.label}
                textAnchor="middle"
                dominantBaseline="central"
              >
                {mark.label}
              </text>
            ) : null}
          </g>
        );
      })}

      {[...pointerGroups.entries()].map(([i, group]) =>
        group.map((pointer, depth) => {
          const top = pointerTop + depth * LAYOUT.pointerRow;
          const color = toneColor(pointer.tone);
          // A null pointer sits over the ∅ rather than over any node.
          const px = i === NULL_SLOT ? nullX : cx(i);
          return (
            <g key={`ptr-${i}-${pointer.name}-${depth}`}>
              <text
                x={px}
                y={top + 6}
                fill={color}
                fontSize={FONT.label}
                textAnchor="middle"
                dominantBaseline="central"
              >
                {pointer.name}
              </text>
              <PointerTip x={px} y={top + 13} color={color} up={false} />
            </g>
          );
        }),
      )}
    </svg>
  );
}

export default LinkedListRenderer;
